<template>
  <div class="text-static-default-hi">
    <Breadcrumb v-if="!pending" :models-names />
    <ModelsComparisonResultsSkeleton v-if="pending" class="pt-32" />
    <RevContainer v-else class="bg-surface-default-low pb-56 pt-32 md:pb-72">
      <h1 class="heading-1 text-center">
        {{ title }}
      </h1>
      <Transition
        enter-active-class="ease-out duration-150"
        enter-from-class="-translate-y-full"
        enter-to-class="translate-y-0"
        leave-active-class="ease-in duration-150"
        leave-from-class="translate-y-0"
        leave-to-class="-translate-y-full"
      >
        <div
          v-show="isScrolled"
          class="bg-surface-default-low border-static-default-low sticky left-0 right-0 top-0 z-[1] border-b"
        >
          <div
            v-show="isScrolled"
            class="bg-surface-default-low border-static-default-low sticky left-0 right-0 top-0 z-[1] border-b"
          >
            <div
              class="mx-auto grid grid-cols-2 gap-x-24 pb-12 pt-16 lg:gap-x-40 lg:pb-24"
            >
              <ModelsComparisonProductCard
                v-for="{ product } in products"
                :key="`sticky-card-${product.productId}`"
                :product
                sticky
              />
            </div>
          </div>
        </div>
      </Transition>
      <div class="mx-auto mt-24">
        <div ref="bodyContainer" class="grid grid-cols-2 gap-x-24 lg:gap-x-40">
          <ModelsComparisonProductCard
            v-for="{ product } in products"
            :key="`card-${product.productId}`"
            :product
          />
        </div>
        <ProductComparisonTableContent
          class="mt-16"
          :nb-columns="2"
          :table-data
        />
      </div>
      <div v-if="relatedArticles?.length > 0" class="mt-48 text-left">
        <h2 class="heading-3 mb-16">
          {{ i18n(translations.relatedArticlesTitle) }}
        </h2>

        <ArticleCardsCarousel :articles="relatedArticles" />
      </div>
      <div v-if="relatedComparePages?.length > 0" class="mt-48 text-left">
        <h2 class="heading-3 mb-16">
          {{ i18n(translations.relatedComparePagesTitle) }}
        </h2>

        <ArticleCardsCarousel :articles="relatedComparePages" />
      </div>
    </RevContainer>
  </div>
</template>

<script lang="ts" setup>
import { showError } from '#imports'
import { computed, ref } from 'vue'

import ArticleCardsCarousel from '@backmarket/nuxt-layer-cms/ArticleCard/ArticleCardsCarousel.vue'
import ProductComparisonTableContent from '@backmarket/nuxt-layer-recommendation/ProductComparisonTableContent.vue'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevContainer } from '@ds/components/Container'
import { useIntersectionObserver } from '@vueuse/core'

import { useAddCanonicalLink } from '~/scopes/seo/composables/useAddCanonicalLink'

import Breadcrumb from '../../components/Breadcrumb/Breadcrumb.vue'
import { useFetchModelsComparisonV2 } from '../../composables/useFetchModelComparisonV2'
import { useModelsComparisonHead } from '../../composables/useModelsComparisonHeadV2'

import ModelsComparisonProductCard from './ModelsComparisonProductCard.vue'
import ModelsComparisonResultsSkeleton from './ModelsComparisonResultsSkeleton.vue'
import translations from './ModelsComparisonResultsV2.translations'

const { products, error, tableData, pending, articlesList } =
  useFetchModelsComparisonV2()
const isScrolled = ref(false)

const i18n = useI18n()
const bodyContainer = ref()

if (error?.value)
  showError({
    statusCode: error.value.statusCode,
    statusMessage: error.value.statusMessage,
  })

const title = computed(() =>
  i18n(translations.resultsTitle, {
    firstModel: products.value?.[0].product.title,
    secondModel: products.value?.[1].product.title,
  }),
)

const modelsNames = computed(() => {
  return products.value?.map(
    (p: { product: { title: string } }) => p.product.title,
  )
})

const relatedArticles = computed(
  () =>
    articlesList.value?.find(
      (articlesItem: { listId: string }) =>
        articlesItem.listId === 'cp_related_articles',
    )?.articles ?? [],
)
const relatedComparePages = computed(
  () =>
    articlesList.value?.find(
      (articlesItem: { listId: string }) =>
        articlesItem.listId === 'cp_related_compare_pages',
    )?.articles ?? [],
)

useIntersectionObserver(
  bodyContainer,
  ([{ boundingClientRect }]) => {
    isScrolled.value = boundingClientRect.top < -100
  },
  {
    threshold: [0, 1],
  },
)
const productNames = computed(() => {
  if (!products.value || products.value.length < 2) {
    return null
  }

  return [products.value[0].product.title, products.value[1].product.title]
})
useModelsComparisonHead(productNames)

useAddCanonicalLink()
</script>
